import { AnimatedFeedbackParams, Coordinate, EmberRosterRole, Entity } from "game-common/models"
import { StatusHaloTier } from "./member_status_halo"
import { Callback } from "game-common/util"
import type { HighlightFrame } from "./entity_highlight"
import type { GroundFoot } from "../effects/grass_feet"
import { Container, Graphics } from "pixi.js"

import { WorldOverlayItem } from "../world_overlay_surface"

// How one spoken line should be presented.
export interface SpeechOptions {
    // Draw the whole line at once, no typewriter. Set for the follow-cam PiP focus, whose render
    // texture makes the progressive reveal flicker.
    instant?: boolean
    // The bubble WAITS for the player: it grows a "Next" button and does not fade on a timer. Lines
    // that arrive while one is held queue behind it (see AvatarHud.spoke).
    hold?: boolean
    // Drop the current held bubble and show this line now. The server has already advanced the
    // matching scene hold because the player completed its gameplay action.
    autoAdvance?: boolean
    // Run when the player dismisses a held line. This is what lets the SPEAKER pace itself off the
    // player's reading speed -- ClientGameLogic sends the server a SPEECH_DISMISSED player event.
    onDismissed?: Callback<void>
    // Answers offered on the line: a button each in place of Next, left to right, the first drawn as
    // the one to take. The line waits for one and never presses itself; a press takes the bubble down
    // and calls onChoice with that answer's id. The messenger asks this way (client/src/messenger).
    choices?: { id: string; label: string }[]
    onChoice?: (id: string) => void
    // The line is an INVITATION the player is free to ignore. It is not a line waiting to be read: the
    // HUD does not step back for it, and the screen still counts as quiet while it is up.
    invitation?: boolean
}

export class BaseRenderable extends Graphics {
    parentArea: BaseRenderable
    active: boolean
    // Guards against attachClickHandler binding pointer listeners more than once on
    // the same renderable (update() re-invokes it whenever entity.clickable changes).
    interactionsBound: boolean
    defaultZIndex: number = 0
    sideCar: BaseRenderable

    doMovement(entity?: Entity) {
        //
    }

    /**
     * Can this draw itself yet? Everything whose art is a texture it already holds answers yes the
     * moment it is constructed; an LPC composite has to fetch and bake its spritesheets first and
     * answers no until it has (see LpcComposite.isReadyToShow).
     *
     * Asked of the entities on screen while a freshly loaded room is held behind the curtain -- the
     * difference between opening the screen on a camp and opening it on the ground the camp will be
     * standing on half a second later. See RoomReveal.
     */
    isReadyToShow(): boolean {
        return true
    }

    update(hp: number, hpMax: number) {
        //
    }

    updateName(name: string) {
        //
    }

    updateSubmerged(submerged: boolean) {
        //
    }

    // Only the LPC composite draws a ground ring, so everything else ignores this. See
    // LpcComposite.updatePlayerRing and Entity.invading.
    updatePlayerRing(entity: Entity) {
        //
    }

    setId(id: string) {
        //
    }

    recolor(color: number) {
        //
    }

    updateBlinking(blinking: boolean) {
        //
    }

    updateScale(scale: number) {
        //
    }

    addTag() {
        //
    }

    removeTag() {
        //
    }

    updateSigil(sigil?: string) {
        //
    }

    updateRole(role: EmberRosterRole | null) {
        //
    }

    // Only the LPC composite draws a ground disc, so everything else ignores this -- same as the
    // player ring above it. The TIER is resolved by the rendering manager, which is the only place
    // that can see both of the channels feeding it. See MemberStatusHalo.
    updateStatusHalo(tier?: StatusHaloTier) {
        //
    }

    remove() {
        this.parent?.removeChild(this)
    }

    detonate() {
        //
    }

    updateLevel(level: number) {
        //
    }

    doAttack(entity: Entity, callback?: Callback<void>, aimAt?: Coordinate) {
        //
    }

    cleanup() {
        // Override in subclasses for cleanup logic
    }

    updateActivation(entity: Entity, active: boolean, callback?: Callback<void>) {
        //
    }

    doDamaged(entity: Entity) {
        //
    }

    spoke(message: string, options?: SpeechOptions) {
        //
    }

    // Drop a held bubble and anything queued behind it, without acking the speaker (see
    // AvatarHud.releaseHeldSpeech).
    releaseHeldSpeech() {
        //
    }

    // An answer on a held bubble that asked something, as the player's press does (AvatarHud.chooseAnswer).
    answerHeldSpeech(choiceId: string) {
        //
    }

    // "Next" on a held bubble, as the player's press does (see AvatarHud.advanceHeldSpeech).
    advanceHeldSpeech() {
        //
    }

    getDrawableSurface(): Container {
        return null
        //
    }

    /** The single frame this art is showing now, for the water to reflect; undefined when it has none. */
    reflectionArt(): import("pixi.js").DisplayObject | undefined {
        return undefined
    }

    /** Ground contact in the world surface's coordinates, when this art has feet. */
    grassContact(): { x: number; y: number; scale: number; angle?: number; lift?: number; feet?: GroundFoot[]; shadow?: { alpha: number; color: number } } | undefined {
        return undefined
    }

    updatePaperdoll(entity: Entity) {
        //
    }

    updateHalo(entity: Entity, enabled: boolean) {
        //
    }

    updateSpeed(factor: number) {
        //
    }

    fragment(entity: Entity, callback?: Callback<void>) {
        //
    }

    updateRotating(rotating: boolean) {
        //
    }

    /** A spent arrow's pose: which way it points and how steeply it stands in the ground (BasicSprite). */
    poseLandedArrow(heading: number, pitchDegrees?: number) {
        //
    }

    onMouseOver(show: boolean) {
        //
    }

    // Warm outline glow shown while this entity is the one the player is pointing at (or, on touch,
    // the nearest interactable). Returns false when this renderable has no art of its own to trace --
    // an invisible marker standing on tile-drawn art -- so the caller can outline the map tiles
    // instead. See EntityHighlight.
    occlusionBody(): { container: Container; frame: HighlightFrame } | undefined {
        return undefined
    }

    setHighlighted(on: boolean): boolean {
        return false
    }

    fadeIn() {
        this.alpha = 0
        const t = setInterval(() => {
            this.alpha += 0.1
            if (this.alpha >= 1.0) {
                this.alpha = 1.0
                clearInterval(t)
            }
        }, 25)
    }

    fadeOut(callback?: Callback<void>) {
        const t = setInterval(() => {
            this.alpha -= 0.1
            if (this.alpha <= 0.0) {
                this.alpha = 0
                clearInterval(t)
                if (callback) {
                    callback()
                }
            }
        }, 25)
    }

    getClickSurface(): BaseRenderable {
        return this
    }

    setWorldOverlayItem(item: WorldOverlayItem) {
        //
    }

    updatePointerIcon(show: boolean, angle?: number) {
        //
    }

    feedback(entity: Entity, image: string, label: string, params?: AnimatedFeedbackParams) {
        //
    }

    updateSpriteId(spriteId: string) {
        //
    }

    updateAfk(afk: boolean) {
        //
    }

    addSidecar(entity: Entity) {
        if (entity.debug) {
            let g = this.sideCar
            if (!g) {
                g = new BaseRenderable()
                this.parent.addChild(g)
                this.sideCar = g
            }

            g.clear()
            g.beginFill(0xff0000)
            g.alpha = 0.6
            const bounds = entity.getCollisionBounds()
            // console.log(bounds.x1, bounds.y1, bounds.width, bounds.height)
            g.drawRect(bounds.x1, bounds.y1, bounds.width, bounds.height)
            g.endFill()
        }
    }

    getYOffset() {
        return -this.height * 0.25
    }

    updateRenderedLocationFrom(entity: Entity) {
        this.x = entity.location.x
        this.y = entity.location.y + this.getYOffset()

        this.addSidecar(entity)
    }

    getCurrentColor = () => this.tint
}
